import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';

// Component
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { checkCompanyLogistics, renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';

import { IListButtonItem } from 'src/components/common/ButtonGroupFilter';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryBuyersAndSuppliersDetail,
  useQueryBuyersProportion,
  useQueryListPartnersFromCompany,
} from 'src/services/queries/useQueryTradeData';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { TypeQueryParamPortTransaction } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import dayjs from 'dayjs';
import { every, get, isEmpty, pickBy, replace } from 'lodash';
import queryString from 'query-string';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipLogisticCompany from 'src/components/common/TooltipLogisticCompany';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import LinkToCompanyDetail, { IForwardedParamsCompanyDetailLink } from 'src/components/LinkToCompanyDetail';
import ModalComponent from 'src/components/Modal/ModalComponent';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { formatValueUSD } from 'src/utils/helpers/utilities';

const SupplierBuyer = ({ selectedTab }: { selectedTab: TypeTabTradeData }) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState<IListButtonItem['key']>(tradeDataUnitByFilter.value);
  const { checkRestriction } = useProvisionRestriction();

  const [openBuyerList, setOpenBuyerList] = useState<boolean>(false);
  const { setSort, buyerSortParams, supplierSortParams } = useTradeDataSortStore();
  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const theme = useTheme();
  const { setSort: _, buyerPopupTableSortParams, supplierPopupTableSortParams } = useTradeDataSortStore();

  const isBuyer = selectedTab === TypeTabTradeData.Buyers;

  const tableSortParams = isBuyer ? buyerSortParams : supplierSortParams;
  const popupTableSortParams = isBuyer ? buyerPopupTableSortParams : supplierPopupTableSortParams;
  const tableSortParamsName = isBuyer ? 'buyerSortParams' : 'supplierSortParams';
  const popupTableSortParamsName = isBuyer ? 'buyerPopupTableSortParams' : 'supplierPopupTableSortParams';
  const countryCodeFieldName = 'company_country_code';
  const companyNameFieldName = 'company_name';
  const companyIdFieldName = 'company_id';

  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  useEffect(() => {
    setProportionTablePaging({
      page: 0,
      page_size: 10,
    });
  }, [selectedTab]);

  const [popupTablePaging, setPopupTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  useEffect(() => {
    if (!openBuyerList) {
      setPopupTablePaging({
        page: 0,
        page_size: 10,
      });
    }
  }, [openBuyerList]);

  const proportionChartRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: 10,
      offset: 0,
      is_buyer: isBuyer,
      sort_by: unitFilter,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: 'desc',
    };
  }, [queryParams, isBuyer, unitFilter, selectedRangeTime]);

  const proportionTableRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: proportionTablePaging.page_size,
      offset: proportionTablePaging.page,
      is_buyer: isBuyer,
      sort_by: tableSortParams?.sortBy,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      order: tableSortParams?.sort,
    };
  }, [queryParams, isBuyer, tableSortParams, selectedRangeTime, proportionTablePaging]);

  const popupTableRequestPayload = useMemo(() => {
    return {
      ...queryParams,
      limit: popupTablePaging.page_size,
      offset: popupTablePaging.page,
      is_buyer: isBuyer,
      sort_by: popupTableSortParams?.sortBy,
      order: popupTableSortParams?.sort,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
    };
  }, [queryParams, isBuyer, selectedRangeTime, popupTableSortParams, popupTablePaging]);

  const [selectedRecord, setSelectedRecord] = useState(undefined as undefined | ICompanyAggregatedByBOL);
  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(proportionChartRequestPayload);

  const { data: proportionalTableData, isFetching: proportionalTableIsFetching } = useQueryBuyersAndSuppliersDetail(
    acceptCreditPayment ? proportionTableRequestPayload : undefined,
  );
  const {
    isFetching: isFetchingChart,
    data: { data: chartData },
  } = useQueryBuyersProportion(acceptCreditPayment ? proportionChartRequestPayload : undefined);

  const {
    data: popupTableData,
    isFetching: popupTableDataIsFetching,
    dataMapping: popupTableDataMapping,
  } = useQueryListPartnersFromCompany(
    popupTableRequestPayload,
    selectedRecord?.company_id || '',
    isBuyer ? TypeBuyerSupplier.supplier : TypeBuyerSupplier.buyer,
  );

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: proportionalTableData,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });
  const [shouldShowLoadingPopup] = useCreditFlow({
    response: popupTableData,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
    cbClose: () => handleCloseBuyerList(),
  });

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [chartData]);

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (isFetchingChart || tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)) return;
    const isEmptyData = isEmpty(chartData) || every(chartData, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [chartData]);

  const handleCloseBuyerList = () => {
    setSelectedRecord(undefined);
    setOpenBuyerList(false);
  };

  const renderTitle = () => {
    switch (isBuyer) {
      case false:
        return t('trade_data.supplier_country.buyers_list');
      case true:
        return t('trade_data.supplier_country.suppliers_list');
    }
  };

  const proportionTableConfigColumn: ConfigCell<ICompanyAggregatedByBOL>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: isBuyer ? 'buyer_name' : 'supplier_name',
      label: isBuyer ? t('trade_data.buyers.table.buyers') : t('trade_data.buyers.table.suppliers'),
      cell: (data: ICompanyAggregatedByBOL) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.[countryCodeFieldName] || ''} />
            <LinkToCompanyDetail
              id={data?.[companyIdFieldName] || ''}
              companyName={data?.[companyNameFieldName] || ''}
              params={queryParams}
              viewedAsType={isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
            />
            {checkCompanyLogistics(data?.[companyNameFieldName] || '') && <TooltipLogisticCompany />}
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={0.5} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'suppliers',
      label: isBuyer
        ? t('trade_data.port_origin.table.total_suppliers')
        : t('trade_data.port_origin.table.total_buyers'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography
          variant="body2"
          color="secondary"
          onClick={() => {
            setSelectedRecord(data);
            setOpenBuyerList(true);
          }}
        >
          {renderNumberOrDefault(isBuyer ? data.suppliers : data.buyers)}
        </Typography>
      ),
    },
  ];

  const renderChart = () => {
    if (isEmpty(chartData) && !isFetchingChart && !shouldShowBillableDataLoading) {
      return (
        <CompanyInfoNoData
          title={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
          content={
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          }
          sxContainer={{
            borderWidth: 0,
          }}
        />
      );
    }
    return (
      <TreemapChart
        loading={shouldShowBillableDataLoading || isFetchingChart}
        colors={TREEMAP_PALLET_COLOR}
        height={600}
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find((item) => item[companyIdFieldName] === point?.name);
          const countryCode = transactionDetail?.[countryCodeFieldName];
          const companyName = transactionDetail?.[companyNameFieldName];
          const value = get(transactionDetail, `${unitFilter}`, 0);
          const valuePercent = get(transactionDetail, `percentage_${unitFilter}`, 0);
          const flag = getEmojiFlag(countryCode || '');
          return `
            <div style="min-width: 270px">
              <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                ${valuePercent}%
              </span>
              <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
              ${flag} ${companyName || t('global.others')}
              </span>
              <span style="color: inherit; display: block; color: ${theme.palette.text.secondary}">${t('trade_data.filter_chart.unit.' + unitFilter)} <b style="color: #fff">
             ${renderNumberOrDefault(value)}</b>
              </span>
            </div>`;
        }}
        dataLabelFormatter={function () {
          const point: Highcharts.Point = this.point;
          const transactionDetail = chartData?.find((item) => item[companyIdFieldName] === point?.name);
          const countryCode = transactionDetail?.[countryCodeFieldName];
          const companyName = transactionDetail?.[companyNameFieldName];
          const flag = getEmojiFlag(countryCode || '');
          const forwardedParams: IForwardedParamsCompanyDetailLink = pickBy(
            {
              start_date: selectedRangeTime?.fromTime,
              end_date: selectedRangeTime?.toTime,
              viewedAsType: isBuyer ? 'buyer' : 'supplier',
            },
            Boolean,
          );
          const query = '?' + queryString.stringify(forwardedParams);

          const url = transactionDetail?.[companyIdFieldName]
            ? AuthRouteKeys.CompanyDetail.replace(':id?', transactionDetail?.[companyIdFieldName] || '') + query
            : '';

          return `<div style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}">
            <a style="font-size: 14px; font-weight: 500; color: ${theme.palette.secondary.main}" href="${url}">
              <p style="display: block">${flag}</p>
              <p style="display: block">${companyName}</p>
            </a>
          </div>`;
        }}
        data={getArrayDefault(chartData).map((transaction, index) => ({
          name: transaction?.[companyIdFieldName],
          value: Math.cbrt(Number(get(transaction, unitFilter, ''))),
          sortIndex: index,
        }))}
      />
    );
  };

  const popupTableConfigColumn: ConfigCell<IBuyerInfo>[] = [
    {
      disablePadding: false,
      id: 'supplier_name',
      label: renderTitle(),
      cell: (data: IBuyerInfo) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <LinkToCompanyDetail
              id={data?.id || ''}
              companyName={data?.company_name || ''}
              params={queryParams}
              viewedAsType={TypeBuyerSupplier.supplier}
            />
            {checkCompanyLogistics(data?.company_name || '') && <TooltipLogisticCompany />}
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.shipments)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: IBuyerInfo) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.teu)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={0.5} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IBuyerInfo) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color="text.secondary">
            {formatValueUSD(data.value_usd)}
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
    // {
    //   disablePadding: false,
    //   id: 'total_buyers',
    //   label: t('trade_data.port_origin.table.total_buyers'),
    //   width: 150,
    //   cell: (data: IBuyerInfo) => (
    //     <Link
    //       to={{
    //         pathname: AuthRouteKeys.CompanyProfiles + `/${data.id}`,
    //       }}
    //     >
    //       <Typography variant="body2" color={'secondary'}>
    //         {'Match Data'}
    //       </Typography>
    //     </Link>
    //   ),
    // },
  ];

  if (
    !proportionalTableIsFetching &&
    !isFetchingChart &&
    isEmpty(getArrayDefault(proportionalTableData.data)) &&
    isEmpty(getArrayDefault(chartData))
  ) {
    return (
      <CompanyInfoNoData
        title={hasEnoughCredit ? t('trade_data.common.notice_no_data.title') : 'Not enough credit'}
        content={
          hasEnoughCredit ? (
            <>{t('trade_data.common.notice_no_data.sub_title')}</>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      />
    );
  }

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <FilterChart
            onSelectTimeRange={({ fromTime, toTime }) => {
              setSelectedRangeTime({
                fromTime: fromTime,
                toTime: toTime,
              });
            }}
            title={isBuyer ? t('trade_data.buyers_proportion') : t('trade_data.suppliers_proportion')}
            onSelectUnitBy={(unit: IListButtonItem['key']) => {
              setSort(tableSortParamsName, {
                sortBy: unit,
                sort: 'desc',
              });
              setUnitFilter(unit);
            }}
            selectedUnitBy={unitFilter}
            selectedRangeTime={{
              fromTime: proportionChartRequestPayload.start_date || '',
              toTime: proportionChartRequestPayload.end_date || '',
            }}
          />
          {renderChart()}
        </Stack>
      </WhiteBoxContent>

      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Typography display={'flex'} justifyContent={'space-between'} textAlign="left" variant="h5">
            {isBuyer ? t('trade_data.buyers.table.title') : t('trade_data.suppliers.table.title')}
            <ExportDataTable
              fileName={isBuyer ? 'list-buyers' : 'list-suppliers'}
              featureId={isBuyer ? LIST_FEATURES.TRADE_DATA_BUYERS : LIST_FEATURES.TRADE_DATA_SUPPLIERS}
              endpoint={
                isBuyer
                  ? get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_BUYERS)
                  : get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_SUPPLIERS)
              }
              paramsExport={proportionTableRequestPayload}
              totalRecordsAvailable={proportionalTableData?.total_records || 0}
            />
          </Typography>
          <Table
            handleSort={(data) => {
              setSort(tableSortParamsName, data);
            }}
            maxRow={50}
            onChangePage={(value) => {
              setProportionTablePaging({ ...proportionTablePaging, page: value });
            }}
            onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || proportionalTableIsFetching}
            page={proportionalTableData.page}
            pageSize={proportionalTableData.page_size}
            totalRecords={proportionalTableData.total_records}
            dataKey={isBuyer ? 'buyer_id' : 'supplier_id'}
            data={proportionalTableData.data}
            configColumn={proportionTableConfigColumn}
          />
        </Stack>
      </WhiteBoxContent>

      <ModalComponent padding={0} pt={0} open={openBuyerList} handleClose={handleCloseBuyerList}>
        <Stack direction="column" spacing={3} width="80vw" bgcolor={theme.palette.background.paper} p={4}>
          <Stack alignItems={'center'} direction="row" justifyContent="space-between">
            <Typography variant="h5">{renderTitle()}</Typography>
            {queryParams.hs_code ? (
              <Typography variant="body2" color="text.secondary">
                This is a list of {!isBuyer ? 'buyers' : 'suppliers'} with HS code {popupTableRequestPayload.hs_code}{' '}
                from {dayjs(popupTableRequestPayload.start_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)} until{' '}
                {dayjs(popupTableRequestPayload.end_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This is a list of {!isBuyer ? 'buyers' : 'suppliers'} with production description{' '}
                <b>{popupTableRequestPayload.keyword}</b> from{' '}
                {dayjs(popupTableRequestPayload.start_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)} until{' '}
                {dayjs(popupTableRequestPayload.end_date).format(FORMAT_DATE_TIME.MMM_DD_YYYY)}
              </Typography>
            )}
            <ExportDataTable
              fileName={isBuyer ? 'buyers-supplier-list' : 'suppliers-buyer-list'}
              paramsExport={popupTableRequestPayload}
              featureId={LIST_FEATURES.INTERNAL_TRADE_DATA_SUB_LIST_SUPPLIERS_BUYERS}
              endpoint={
                isBuyer
                  ? replace(
                      get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_BUYERS_SUPPLIER_LIST),
                      ':id',
                      selectedRecord?.company_id ?? '',
                    )
                  : replace(
                      get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_SUPPLIERS_BUYER_LIST),
                      ':id',
                      selectedRecord?.company_id ?? '',
                    )
              }
              totalRecordsAvailable={popupTableData?.total_records || 0}
            />
          </Stack>
          {isEmpty(popupTableDataMapping) && !popupTableDataIsFetching ? (
            <Alert severity="info" color="info">
              Withheld by Government
            </Alert>
          ) : (
            <Table
              maxRow={10}
              handleSort={(popupTableData) => {
                setSort(popupTableSortParamsName, popupTableData);
              }}
              onChangePage={(value) => {
                setPopupTablePaging({ ...popupTablePaging, page: value });
              }}
              onChangePageSize={(value) => setPopupTablePaging({ page: 0, page_size: value })}
              loading={shouldShowLoadingPopup || popupTableDataIsFetching}
              page={popupTableData?.page}
              pageSize={popupTableData?.page_size}
              totalRecords={popupTableData?.total_records}
              dataKey={'id'}
              data={popupTableDataMapping}
              configColumn={popupTableConfigColumn}
              pagingTooltipsText={
                (selectedRecord?.[!isBuyer ? 'buyers' : 'suppliers'] || 0) > (popupTableData?.total_records || 9999999)
                  ? 'trade_data.count_mismatch.tooltip'
                  : undefined
              }
            />
          )}
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default SupplierBuyer;
